import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  Linkedin,
  Plus,
  Send,
  Calendar,
  Clock,
  Sparkles,
  Upload,
  Trash2,
  Eye,
  Edit3,
  Building2,
  FileText,
  TrendingUp,
  Award,
  Megaphone,
  Users,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  XCircle,
  Link2,
  Unlink,
  ChevronRight,
  Image as ImageIcon,
  Mail,
  UserPlus,
  Globe
} from "lucide-react";


const NEWSLETTER_TYPES = [
  { value: "industry_news", label: "Industry News", icon: TrendingUp, color: "text-blue-500" },
  { value: "company_update", label: "Company Update", icon: Building2, color: "text-purple-500" },
  { value: "milestone", label: "Milestone", icon: Award, color: "text-amber-500" },
  { value: "product_launch", label: "Product Launch", icon: Megaphone, color: "text-green-500" },
  { value: "event", label: "Event", icon: Calendar, color: "text-rose-500" },
  { value: "thought_leadership", label: "Thought Leadership", icon: Users, color: "text-indigo-500" },
];

const STATUS_STYLES = {
  draft: { bg: "bg-slate-100", text: "text-slate-700", icon: Edit3 },
  scheduled: { bg: "bg-amber-100", text: "text-amber-700", icon: Clock },
  published: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle },
  failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
};

export default function LinkedInNewsletterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const token = localStorage.getItem("token");

  // Connection state
  const [linkedInConnection, setLinkedInConnection] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(true);

  // Newsletter state
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(null);

  // AI Draft Generator state
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [appInsights, setAppInsights] = useState(null);
  const [aiForm, setAiForm] = useState({
    topic: "",
    newsletter_type: "industry_news",
    tone: "professional",
    use_app_context: true,
    use_trending: true,
    focus_areas: []
  });

  // Suggested images state
  const [suggestedImages, setSuggestedImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // Subscriber management state
  const [showSubscribers, setShowSubscribers] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [newSubscriberEmail, setNewSubscriberEmail] = useState("");
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);

  // Publish options state
  const [publishTarget, setPublishTarget] = useState("both"); // linkedin_only, email_only, both
  const [sendingEmail, setSendingEmail] = useState(false);

  // Composer form state - now with separate LinkedIn and Email content
  const [form, setForm] = useState({
    title: "",
    linkedin_post_text: "",  // Short version for LinkedIn
    email_content: "",       // Long version for email newsletter
    newsletter_type: "company_update",
    tags: [],
    image_url: "",
    scheduled_at: "",
  });
  const [tagInput, setTagInput] = useState("");

  // OAuth error state for detailed display
  const [oauthError, setOauthError] = useState(null);

  // Check for OAuth callback
  useEffect(() => {
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    
    if (connected === "true") {
      toast.success("LinkedIn account connected successfully!");
      // Clear URL params
      navigate("/linkedin-newsletter", { replace: true });
    } else if (error) {
      // Decode and display the error
      const decodedError = decodeURIComponent(error);
      setOauthError(decodedError);
      toast.error("LinkedIn connection failed - see details below");
      navigate("/linkedin-newsletter", { replace: true });
    }
  }, [searchParams, navigate]);

  // Fetch LinkedIn connection status
  const fetchConnection = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/linkedin-newsletter/connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkedInConnection(response.data);
    } catch (error) {
      console.error("Error fetching LinkedIn connection:", error);
    } finally {
      setConnectionLoading(false);
    }
  }, [token]);

  // Fetch newsletters
  const fetchNewsletters = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/linkedin-newsletter/newsletters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewsletters(response.data.newsletters || []);
    } catch (error) {
      console.error("Error fetching newsletters:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchConnection();
      fetchNewsletters();
    }
  }, [token, fetchConnection, fetchNewsletters]);

  // Connect LinkedIn
  const handleConnectLinkedIn = async () => {
    try {
      const response = await axios.get(`${API_URL}/linkedin-newsletter/oauth/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to LinkedIn OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      if (error.response?.data?.detail?.includes("not configured")) {
        toast.error("LinkedIn API credentials not configured. Please contact administrator.");
      } else {
        toast.error("Failed to initiate LinkedIn connection");
      }
    }
  };

  // Disconnect LinkedIn
  const handleDisconnectLinkedIn = async () => {
    if (!window.confirm("Are you sure you want to disconnect your LinkedIn account?")) return;
    
    try {
      await axios.delete(`${API_URL}/linkedin-newsletter/connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkedInConnection({ connected: false });
      toast.success("LinkedIn account disconnected");
    } catch (error) {
      toast.error("Failed to disconnect LinkedIn");
    }
  };

  // Create/Update newsletter
  const handleSaveNewsletter = async (publish = false) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      let newsletter;
      
      if (editingNewsletter) {
        const response = await axios.put(
          `${API_URL}/linkedin-newsletter/newsletters/${editingNewsletter.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        newsletter = response.data;
        toast.success("Newsletter updated");
      } else {
        const response = await axios.post(
          `${API_URL}/linkedin-newsletter/newsletters`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        newsletter = response.data;
        toast.success("Newsletter created");
      }

      if (publish && linkedInConnection?.connected) {
        await handlePublish(newsletter.id);
      }

      setShowComposer(false);
      setEditingNewsletter(null);
      resetForm();
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save newsletter");
    }
  };

  // Generate LinkedIn post with AI
  const handleGeneratePost = async () => {
    if (!form.title && !form.email_content) {
      toast.error("Add title or email content first");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/linkedin-newsletter/generate-linkedin-post`,
        {
          title: form.title,
          content: form.email_content || form.title,
          newsletter_type: form.newsletter_type,
          tone: "professional",
          include_hashtags: true,
          max_length: 2800
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setForm(prev => ({
        ...prev,
        linkedin_post_text: response.data.linkedin_post_text
      }));
      toast.success(`Generated ${response.data.character_count} character post`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate post");
    } finally {
      setGenerating(false);
    }
  };

  // Publish to LinkedIn
  const handlePublish = async (newsletterId) => {
    if (!linkedInConnection?.connected) {
      toast.error("Connect LinkedIn first");
      return;
    }

    const organizations = linkedInConnection.organizations || [];
    if (organizations.length === 0) {
      toast.error("No LinkedIn organizations available");
      return;
    }

    // Get organization URN
    const orgUrn = organizations[0].organization || organizations[0].organizationalTarget;
    if (!orgUrn) {
      toast.error("Invalid organization data");
      return;
    }

    setPublishing(newsletterId);
    try {
      await axios.post(
        `${API_URL}/linkedin-newsletter/newsletters/${newsletterId}/publish`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { organization_urn: orgUrn }
        }
      );
      toast.success("Published to LinkedIn!");
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish");
    } finally {
      setPublishing(null);
    }
  };

  // Delete newsletter
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this newsletter?")) return;

    try {
      await axios.delete(`${API_URL}/linkedin-newsletter/newsletters/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Newsletter deleted");
      fetchNewsletters();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Import from email
  const handleEmailImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API_URL}/linkedin-newsletter/import-from-email`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setForm(prev => ({
        ...prev,
        title: response.data.title || prev.title,
        content: response.data.content || prev.content,
        newsletter_type: response.data.suggested_type || prev.newsletter_type
      }));
      toast.success("Imported from email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import email");
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      linkedin_post_text: "",
      email_content: "",
      newsletter_type: "company_update",
      tags: [],
      image_url: "",
      scheduled_at: "",
      activeTab: "linkedin"
    });
    setTagInput("");
    setSuggestedImages([]);
  };

  const openComposer = (newsletter = null) => {
    if (newsletter) {
      setEditingNewsletter(newsletter);
      setForm({
        title: newsletter.title || "",
        linkedin_post_text: newsletter.linkedin_post_text || "",
        email_content: newsletter.email_content || newsletter.content || "",
        newsletter_type: newsletter.newsletter_type || "company_update",
        tags: newsletter.tags || [],
        image_url: newsletter.image_url || "",
        scheduled_at: newsletter.scheduled_at || "",
        activeTab: "linkedin"
      });
    } else {
      setEditingNewsletter(null);
      resetForm();
    }
    setShowComposer(true);
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // AI Draft Generator Functions
  const fetchTrendingTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/linkedin-newsletter/ai/trending-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrendingTopics(response.data.trending_topics || []);
    } catch (error) {
      console.error("Error fetching trending topics:", error);
    }
  };

  const fetchAppInsights = async () => {
    try {
      const response = await axios.get(`${API_URL}/linkedin-newsletter/ai/app-insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppInsights(response.data);
    } catch (error) {
      console.error("Error fetching app insights:", error);
    }
  };

  const openAIGenerator = () => {
    setShowAIGenerator(true);
    fetchTrendingTopics();
    fetchAppInsights();
  };

  const handleAIGenerate = async () => {
    setAiGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/linkedin-newsletter/ai/generate-draft`,
        aiForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const draft = response.data.draft;
      
      // Fill the form with AI-generated content (both LinkedIn and Email versions)
      setForm({
        title: draft.title || "",
        linkedin_post_text: draft.linkedin_post_text || "",
        email_content: draft.email_content || "",
        newsletter_type: draft.newsletter_type || "industry_news",
        tags: draft.tags || [],
        image_url: "",
        scheduled_at: ""
      });
      
      // Fetch suggested images based on the topic/title
      const imageQuery = draft.suggested_image_query || aiForm.topic || draft.title || "logistics shipping";
      fetchSuggestedImages(imageQuery);
      
      setShowAIGenerator(false);
      setShowComposer(true);
      toast.success("AI draft generated with LinkedIn post & email newsletter versions!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate draft");
    } finally {
      setAiGenerating(false);
    }
  };

  const selectTrendingTopic = (topic) => {
    setAiForm(prev => ({ ...prev, topic: topic.topic }));
  };

  // Fetch suggested images based on topic or title
  const fetchSuggestedImages = async (searchQuery) => {
    if (!searchQuery) return;
    setLoadingImages(true);
    try {
      const response = await axios.get(`${API_URL}/linkedin-newsletter/ai/suggested-images`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { query: searchQuery, count: 6 }
      });
      setSuggestedImages(response.data.images || []);
    } catch (error) {
      console.error("Error fetching images:", error);
      // Use fallback images
      setSuggestedImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  // Select an image from suggestions
  const selectSuggestedImage = (imageUrl) => {
    setForm(prev => ({ ...prev, image_url: imageUrl }));
    toast.success("Image selected!");
  };

  // Subscriber Management Functions
  const fetchSubscribers = async () => {
    setLoadingSubscribers(true);
    try {
      const response = await axios.get(`${API_URL}/linkedin-newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const addSubscriber = async () => {
    if (!newSubscriberEmail.trim() || !newSubscriberEmail.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    try {
      await axios.post(
        `${API_URL}/linkedin-newsletter/subscribers`,
        { email: newSubscriberEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Subscriber added");
      setNewSubscriberEmail("");
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add subscriber");
    }
  };

  const removeSubscriber = async (id) => {
    try {
      await axios.delete(`${API_URL}/linkedin-newsletter/subscribers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscriber removed");
      fetchSubscribers();
    } catch (error) {
      toast.error("Failed to remove subscriber");
    }
  };

  // Publish to both LinkedIn and Email
  const handlePublishAll = async (newsletterId) => {
    setSendingEmail(true);
    try {
      const orgUrn = linkedInConnection?.organizations?.[0]?.organization || 
                     linkedInConnection?.organizations?.[0]?.organizationalTarget || "";
      
      const response = await axios.post(
        `${API_URL}/linkedin-newsletter/newsletters/${newsletterId}/publish-all`,
        {
          target: publishTarget,
          organization_urn: orgUrn
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const results = response.data.results;
      if (results.linkedin?.success && results.email?.success) {
        toast.success(`Published to LinkedIn and sent to ${results.email.sent_count} subscribers!`);
      } else if (results.linkedin?.success) {
        toast.success("Published to LinkedIn!");
      } else if (results.email?.success) {
        toast.success(`Sent to ${results.email.sent_count} subscribers!`);
      } else {
        toast.error("Publishing failed");
      }
      
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish");
    } finally {
      setSendingEmail(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Linkedin className="w-16 h-16 mx-auto text-[#0A66C2] mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">LinkedIn Newsletter</h1>
          <p className="text-muted-foreground mb-6">Please log in to access the LinkedIn Newsletter plugin.</p>
          <Button onClick={() => navigate("/login")}>Log In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#0A66C2] rounded-xl">
              <Linkedin className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">LinkedIn Newsletter</h1>
              <p className="text-muted-foreground">Create and publish content to your company page</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {linkedInConnection?.connected ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">
                    Connected as {linkedInConnection.linkedin_name}
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={handleDisconnectLinkedIn}>
                  <Unlink className="w-4 h-4 mr-1" />
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button onClick={handleConnectLinkedIn} className="bg-[#0A66C2] hover:bg-[#004182]">
                <Link2 className="w-4 h-4 mr-2" />
                Connect LinkedIn
              </Button>
            )}
            
            <Button 
              onClick={() => { setShowSubscribers(true); fetchSubscribers(); }}
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
              data-testid="manage-subscribers-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Subscribers ({subscribers.length})
            </Button>
            
            <Button 
              onClick={openAIGenerator} 
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="ai-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Generate
            </Button>
            
            <Button onClick={() => openComposer()} data-testid="create-newsletter-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Newsletter
            </Button>
          </div>
        </div>

        {/* Connection Warning */}
        {!connectionLoading && !linkedInConnection?.connected && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800">LinkedIn Not Connected</h3>
              <p className="text-sm text-amber-700 mt-1">
                Connect your LinkedIn account to publish newsletters directly to your company page.
                You'll need to be an admin of the company page to post.
              </p>
            </div>
          </motion.div>
        )}

        {/* OAuth Error Display */}
        {oauthError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-medium text-red-800">LinkedIn Connection Failed</h3>
                <p className="text-sm text-red-700 mt-1">{oauthError}</p>
                
                <div className="mt-3 p-3 bg-white rounded border border-red-100">
                  <h4 className="font-medium text-red-800 text-sm mb-2">Setup Checklist for Company Page Posting:</h4>
                  <ol className="text-xs text-red-700 space-y-1 list-decimal list-inside">
                    <li>Go to <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">LinkedIn Developer Portal</a></li>
                    <li>Open your app and go to the "Products" tab</li>
                    <li>Request access to "Community Management API" (required for company posting)</li>
                    <li>Wait for approval (may require app verification)</li>
                    <li>Ensure you are an Administrator of the company page you want to post to</li>
                  </ol>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOauthError(null)}
                  className="mt-3"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Newsletter Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : newsletters.length === 0 ? (
          <div className="text-center py-12 bg-surface rounded-xl border border-border">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Newsletters Yet</h3>
            <p className="text-muted-foreground mb-6">
              Create your first newsletter to share industry news and company updates.
            </p>
            <Button onClick={() => openComposer()}>
              <Plus className="w-4 h-4 mr-2" />
              Create Newsletter
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {newsletters.map((newsletter) => {
              const typeInfo = NEWSLETTER_TYPES.find(t => t.value === newsletter.newsletter_type) || NEWSLETTER_TYPES[1];
              const statusInfo = STATUS_STYLES[newsletter.status] || STATUS_STYLES.draft;
              const StatusIcon = statusInfo.icon;
              const TypeIcon = typeInfo.icon;

              return (
                <motion.div
                  key={newsletter.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-surface border border-border rounded-xl p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${statusInfo.bg}`}>
                      <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                      <StatusIcon className="w-3 h-3" />
                      {newsletter.status}
                    </div>
                  </div>

                  <h3 className="font-semibold text-foreground mb-2 line-clamp-2">
                    {newsletter.title}
                  </h3>

                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {newsletter.content?.substring(0, 100)}...
                  </p>

                  {newsletter.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {newsletter.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-3 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openComposer(newsletter)}
                    >
                      <Edit3 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    
                    {newsletter.status !== "published" && linkedInConnection?.connected && (
                      <Button
                        size="sm"
                        onClick={() => handlePublish(newsletter.id)}
                        disabled={publishing === newsletter.id}
                        className="bg-[#0A66C2] hover:bg-[#004182]"
                      >
                        {publishing === newsletter.id ? (
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3 mr-1" />
                        )}
                        Publish
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(newsletter.id)}
                      className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Composer Modal */}
      <AnimatePresence>
        {showComposer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setShowComposer(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-background rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-semibold text-foreground">
                  {editingNewsletter ? "Edit Newsletter" : "Create Newsletter"}
                </h2>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".msg,.eml"
                      onChange={handleEmailImport}
                      className="hidden"
                    />
                    <Button variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-1" />
                        Import Email
                      </span>
                    </Button>
                  </label>
                  <Button variant="ghost" size="sm" onClick={() => setShowComposer(false)}>
                    ✕
                  </Button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 grid grid-cols-2 gap-6">
                {/* Left Column - Content */}
                <div className="space-y-4">
                  <div>
                    <Label>Newsletter Type</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {NEWSLETTER_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.value}
                            onClick={() => setForm(prev => ({ ...prev, newsletter_type: type.value }))}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              form.newsletter_type === type.value
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/50"
                            }`}
                          >
                            <Icon className={`w-4 h-4 mb-1 ${type.color}`} />
                            <span className="text-xs font-medium">{type.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label>Title *</Label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Newsletter title..."
                      className="mt-1"
                    />
                  </div>

                  {/* Tabbed Content Area */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex border-b">
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, activeTab: "linkedin" }))}
                        className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                          form.activeTab !== "email" 
                            ? "bg-blue-50 text-blue-700 border-b-2 border-blue-500" 
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <Linkedin className="w-4 h-4" />
                        LinkedIn Post
                        <span className="text-xs bg-blue-100 px-1.5 py-0.5 rounded">{form.linkedin_post_text?.length || 0}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, activeTab: "email" }))}
                        className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                          form.activeTab === "email" 
                            ? "bg-green-50 text-green-700 border-b-2 border-green-500" 
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <Mail className="w-4 h-4" />
                        Email Newsletter
                        <span className="text-xs bg-green-100 px-1.5 py-0.5 rounded">{form.email_content?.length || 0}</span>
                      </button>
                    </div>
                    
                    <div className="p-4">
                      {form.activeTab !== "email" ? (
                        <div>
                          <p className="text-xs text-slate-500 mb-2">Short version for LinkedIn (500-1000 chars ideal)</p>
                          <Textarea
                            value={form.linkedin_post_text}
                            onChange={(e) => setForm(prev => ({ ...prev, linkedin_post_text: e.target.value }))}
                            placeholder="Write a compelling LinkedIn post with key highlights, call to action, and hashtags..."
                            className="min-h-[200px]"
                          />
                          <div className="flex justify-between mt-2 text-xs text-slate-400">
                            <span>Include hashtags like #Logistics #SupplyChain</span>
                            <span className={form.linkedin_post_text?.length > 3000 ? "text-red-500" : ""}>
                              {form.linkedin_post_text?.length || 0} / 3000
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-xs text-slate-500 mb-2">Long-form content for email newsletter (1500-2500 words ideal)</p>
                          <Textarea
                            value={form.email_content}
                            onChange={(e) => setForm(prev => ({ ...prev, email_content: e.target.value }))}
                            placeholder="Write your detailed email newsletter content. Include sections, insights, data, and a professional sign-off..."
                            className="min-h-[300px]"
                          />
                          <div className="text-xs text-slate-400 mt-2 text-right">
                            ~{Math.round((form.email_content?.length || 0) / 5)} words
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Tags</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        placeholder="Add tag..."
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                      />
                      <Button variant="outline" onClick={addTag}>Add</Button>
                    </div>
                    {form.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-slate-100 rounded-full text-sm flex items-center gap-1"
                          >
                            #{tag}
                            <button onClick={() => removeTag(tag)} className="hover:text-red-500">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Preview & Image */}
                <div className="space-y-4">
                  {/* Preview Card */}
                  <div>
                    <Label className="mb-2 block">Preview</Label>
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                      {form.image_url && (
                        <img src={form.image_url} alt="Preview" className="w-full h-32 object-cover" />
                      )}
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            EP
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900 text-sm">myEuropacific</div>
                            <div className="text-xs text-slate-500">Freight Forwarding & Logistics</div>
                          </div>
                        </div>
                        <h3 className="font-semibold text-slate-800 mb-2">{form.title || "Newsletter Title"}</h3>
                        <p className="text-sm text-slate-600 line-clamp-3">
                          {form.linkedin_post_text?.slice(0, 150) || "Your post preview will appear here..."}
                          {form.linkedin_post_text?.length > 150 && "..."}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Image URL (optional)</Label>
                      {form.title && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchSuggestedImages(form.title)}
                          disabled={loadingImages}
                          className="text-xs text-purple-600 hover:text-purple-700"
                        >
                          {loadingImages ? (
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <ImageIcon className="w-3 h-3 mr-1" />
                          )}
                          Find Images
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={form.image_url}
                        onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                        placeholder="https://..."
                      />
                    </div>
                    
                    {/* Suggested Images Grid */}
                    {suggestedImages.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Suggested images - click to select
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {suggestedImages.map((img) => (
                            <button
                              key={img.id}
                              type="button"
                              onClick={() => selectSuggestedImage(img.url)}
                              className={`relative rounded-lg overflow-hidden border-2 transition-all hover:border-purple-400 ${
                                form.image_url === img.url ? 'border-purple-500 ring-2 ring-purple-200' : 'border-transparent'
                              }`}
                            >
                              <img
                                src={img.thumb || img.url}
                                alt={img.alt || "Suggested image"}
                                className="w-full h-16 object-cover"
                              />
                              {form.image_url === img.url && (
                                <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-purple-600" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Images from Unsplash • Free for commercial use
                        </p>
                      </div>
                    )}
                    
                    {/* Current image preview */}
                    {form.image_url && (
                      <div className="mt-2 rounded-lg overflow-hidden border border-border">
                        <img
                          src={form.image_url}
                          alt="Preview"
                          className="w-full h-40 object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Schedule (optional)</Label>
                    <Input
                      type="datetime-local"
                      value={form.scheduled_at?.slice(0, 16) || ""}
                      onChange={(e) => setForm(prev => ({ ...prev, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Leave empty to save as draft
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer with Publish Options */}
              <div className="px-6 py-4 border-t border-border">
                {/* Publish Target Selection */}
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  <Label className="text-sm font-medium mb-2 block">Publish To:</Label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="publishTarget"
                        value="both"
                        checked={publishTarget === "both"}
                        onChange={(e) => setPublishTarget(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-sm flex items-center gap-1">
                        <Globe className="w-4 h-4 text-slate-600" />
                        Both LinkedIn & Email
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="publishTarget"
                        value="linkedin_only"
                        checked={publishTarget === "linkedin_only"}
                        onChange={(e) => setPublishTarget(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-sm flex items-center gap-1">
                        <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                        LinkedIn Only
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="publishTarget"
                        value="email_only"
                        checked={publishTarget === "email_only"}
                        onChange={(e) => setPublishTarget(e.target.value)}
                        className="text-green-600"
                      />
                      <span className="text-sm flex items-center gap-1">
                        <Mail className="w-4 h-4 text-green-600" />
                        Email Only
                      </span>
                    </label>
                  </div>
                  {publishTarget !== "linkedin_only" && (
                    <p className="text-xs text-slate-500 mt-2">
                      Will send to {subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowComposer(false)}>
                    Cancel
                  </Button>
                  <Button variant="outline" onClick={() => handleSaveNewsletter(false)}>
                    Save as Draft
                  </Button>
                  <Button
                    onClick={async () => {
                      // First save, then publish
                      if (!form.title.trim()) {
                        toast.error("Title is required");
                        return;
                      }
                      try {
                        let newsletter;
                        const payload = { ...form, content: form.email_content };
                        if (editingNewsletter) {
                          const response = await axios.put(
                            `${API_URL}/linkedin-newsletter/newsletters/${editingNewsletter.id}`,
                            payload,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          newsletter = response.data;
                        } else {
                          const response = await axios.post(
                            `${API_URL}/linkedin-newsletter/newsletters`,
                            payload,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          newsletter = response.data;
                        }
                        await handlePublishAll(newsletter.id);
                        setShowComposer(false);
                        setEditingNewsletter(null);
                        resetForm();
                        fetchNewsletters();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to publish");
                      }
                    }}
                    disabled={sendingEmail}
                    className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
                  >
                    {sendingEmail ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Save & Publish
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Draft Generator Modal */}
      <AnimatePresence>
        {showAIGenerator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setShowAIGenerator(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-background rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* AI Modal Header */}
              <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">AI Newsletter Generator</h2>
                    <p className="text-sm text-muted-foreground">Let AI help you create engaging content</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAIGenerator(false)}
                  className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* AI Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Trending Topics */}
                <div>
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    Trending Logistics Topics
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {trendingTopics.map((topic, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectTrendingTopic(topic)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          aiForm.topic === topic.topic
                            ? "border-purple-500 bg-purple-50"
                            : "border-border hover:border-purple-300 hover:bg-purple-50/50"
                        }`}
                      >
                        <div className="font-medium text-sm text-foreground">{topic.topic}</div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{topic.description}</div>
                        <div className="flex gap-1 mt-2">
                          {topic.tags?.slice(0, 2).map((tag, tidx) => (
                            <span key={tidx} className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* App Insights */}
                {appInsights && appInsights.insights?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-green-600" />
                      Insights from Your App
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {appInsights.insights.map((insight, idx) => (
                        <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="font-medium text-sm text-green-800">{insight.title}</div>
                          <div className="text-xs text-green-700 mt-1">{insight.suggestion}</div>
                          {insight.type === "top_routes" && insight.data && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {insight.data.map((r, ridx) => (
                                <span key={ridx} className="text-xs px-2 py-0.5 bg-white border border-green-300 rounded text-green-700">
                                  {r.route} ({r.shipments})
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Topic Input */}
                <div>
                  <Label className="text-foreground">Custom Topic (Optional)</Label>
                  <Input
                    value={aiForm.topic}
                    onChange={(e) => setAiForm(prev => ({ ...prev, topic: e.target.value }))}
                    placeholder="Enter a specific topic or leave empty for AI to suggest"
                    className="mt-1"
                  />
                </div>

                {/* Options */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Newsletter Type</Label>
                    <select
                      value={aiForm.newsletter_type}
                      onChange={(e) => setAiForm(prev => ({ ...prev, newsletter_type: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 border border-input rounded-lg bg-background text-sm"
                    >
                      {NEWSLETTER_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-foreground">Tone</Label>
                    <select
                      value={aiForm.tone}
                      onChange={(e) => setAiForm(prev => ({ ...prev, tone: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 border border-input rounded-lg bg-background text-sm"
                    >
                      <option value="professional">Professional</option>
                      <option value="casual">Casual</option>
                      <option value="engaging">Engaging</option>
                    </select>
                  </div>
                </div>

                {/* Toggles */}
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiForm.use_app_context}
                      onChange={(e) => setAiForm(prev => ({ ...prev, use_app_context: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-foreground">Use app data for context</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiForm.use_trending}
                      onChange={(e) => setAiForm(prev => ({ ...prev, use_trending: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-foreground">Include trending topics</span>
                  </label>
                </div>
              </div>

              {/* AI Modal Footer */}
              <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3 bg-gray-50">
                <Button variant="outline" onClick={() => setShowAIGenerator(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAIGenerate}
                  disabled={aiGenerating}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                >
                  {aiGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Draft
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subscribers Management Modal */}
      <AnimatePresence>
        {showSubscribers && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setShowSubscribers(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-background rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Newsletter Subscribers</h2>
                    <p className="text-sm text-muted-foreground">{subscribers.length} subscribers</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSubscribers(false)}
                  className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Add Subscriber */}
              <div className="px-6 py-4 border-b border-border bg-gray-50">
                <div className="flex gap-2">
                  <Input
                    value={newSubscriberEmail}
                    onChange={(e) => setNewSubscriberEmail(e.target.value)}
                    placeholder="Enter email address..."
                    onKeyPress={(e) => e.key === "Enter" && addSubscriber()}
                    className="flex-1"
                  />
                  <Button onClick={addSubscriber} className="bg-green-600 hover:bg-green-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Subscribers List */}
              <div className="flex-1 overflow-y-auto p-4">
                {loadingSubscribers ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-green-600" />
                  </div>
                ) : subscribers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No subscribers yet</p>
                    <p className="text-sm text-slate-400">Add email addresses to build your list</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {subscribers.map((sub) => (
                      <div
                        key={sub.id}
                        className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-green-200 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Mail className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-800">{sub.email}</div>
                            {sub.name && <div className="text-xs text-slate-500">{sub.name}</div>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {sub.tags?.length > 0 && (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                              {sub.tags[0]}
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSubscriber(sub.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-border bg-gray-50">
                <p className="text-xs text-slate-500 text-center">
                  Subscribers will receive email newsletters when you publish with email enabled
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
